import { useCallback, useRef, useState } from 'react'

import { ApiError, stream } from './api'

type Done = Record<string, unknown>

// Служебные первые строки ответа модели (вердикт проверки, балл судьи) — то же
// правило, что у бота (utils/telegram.without_service_lines). Итог приходит уже
// без них, а в черновике потока они мелькали у ученика до конца ответа.
const СЛУЖЕБНАЯ = /^[\s[*]*(ВЕРДИКТ|БАЛЛ|КРИТЕРИИ)(?![А-Яа-яЁё])/i

export function withoutServiceLines(text: string): string {
  const nl = text.indexOf('\n')
  const первая = nl === -1 ? text : text.slice(0, nl)
  if (СЛУЖЕБНАЯ.test(первая)) {
    return nl === -1 ? '' : withoutServiceLines(text.slice(nl + 1).trimStart())
  }
  return text
}

/**
 * Приём потокового ответа: текст копится по кускам, итог приходит один раз.
 *
 * Ученик видит, как ответ набирается, — это и есть смысл потока. Итоговое
 * событие отдаётся колбэком: по нему экран решает, что показать (кнопку разбора,
 * карточку награды, обновлённое состояние задачи).
 */
export function useStreamRunner() {
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  // Защита от двойного запуска: одна и та же реплика, отправленная дважды,
  // стоила бы двух обращений к модели и запутала бы диалог.
  const busy = useRef(false)

  const reset = useCallback(() => {
    setText('')
    setError('')
  }, [])

  const run = useCallback(
    async (
      path: string,
      init: { body?: unknown; form?: FormData } = {},
      onDone?: (done: Done) => void,
      // Рисунок учебника приходит ПОСЛЕ итога отдельным событием: его поиск
      // не задерживает сам ответ.
      onIllustration?: (img: Done) => void,
    ) => {
      if (busy.current) return
      busy.current = true
      setRunning(true)
      setText('')
      setError('')
      let накоплено = ''
      try {
        for await (const ev of stream(path, init)) {
          if (ev.type === 'chunk') {
            накоплено += ev.text
            setText(withoutServiceLines(накоплено))
          } else if (ev.type === 'error') {
            setError(ev.message)
          } else if (ev.type === 'illustration') {
            onIllustration?.(ev)
          } else {
            // Итог несёт готовый текст без служебных строк — он и остаётся на
            // экране вместо накопленного черновика.
            const готовый = typeof ev.text === 'string' ? ev.text : накоплено
            setText(готовый)
            onDone?.(ev)
            // Ответ готов — экран свободен, хотя поток ещё может донести рисунок
            // (событие illustration). Иначе ответ на эти секунды висел бы дважды
            // (в ленте и черновиком), а отправка оставалась бы заблокированной.
            busy.current = false
            setRunning(false)
          }
        }
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Связь оборвалась. Попробуй ещё раз.')
      } finally {
        busy.current = false
        setRunning(false)
      }
    },
    [],
  )

  return { text, running, error, run, reset }
}
